import { useState } from "react";
import Card from "@rio-cloud/rio-uikit/Card";
import useWindowResize from "@rio-cloud/rio-uikit/useWindowResize";
import { SCREEN_SM } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import DRERow, { getIndentStyle, DRE_TOGGLE_COLUMN_STYLE, type DREIndent } from "./DRERow";
import type { DREHierarchicalLine, DRELineType, DRENodeType, DREParentPath } from "../../../types/dashboard";
import DownloadButton from "./downloadButton";
import { useT } from "@hooks";

const INDENT_WIDE: DREIndent = { base: 25, step: 25, maxLevel: Number.MAX_SAFE_INTEGER };
const INDENT_COMPACT: DREIndent = { base: 10, step: 10, maxLevel: 3 };

export interface ExpandNodeParams {
  nodeId: string;
  compositeId: string;
  nodeType: DRENodeType;
  lineType: DRELineType;
  parentPath?: DREParentPath;
}

interface DRETableProps {
  data: DREHierarchicalLine[];
  expandedItems: string[];
  loadingNodes: string[];
  onToggle: (id: string) => void;
  onExpandNode: (params: ExpandNodeParams) => Promise<void>;
  onDownload?: () => void;
  onExportDRE?: (view: "general" | "branch" | "segment" | "vehicle" | "driver") => void;
}

export default function DRETable({
  data,
  expandedItems,
  loadingNodes,
  onToggle,
  onExpandNode,
  onDownload,
  onExportDRE,
}: Readonly<DRETableProps>) {
  const t = useT();
  const [isCompact, setIsCompact] = useState(() => window.innerWidth < SCREEN_SM);

  useWindowResize(() => setIsCompact(window.innerWidth < SCREEN_SM));

  const indent = isCompact ? INDENT_COMPACT : INDENT_WIDE;

  const handleExport = (view: "general" | "branch" | "segment" | "vehicle" | "driver") => {
    if (onExportDRE) {
      onExportDRE(view);
    }
  };

  return (
    <Card className="padding-y-20 padding-x-15 padding-x-50-md margin-bottom-20">
      <div className="display-flex align-items-center justify-content-between gap-10 padding-bottom-20">
        <span className="text-size-16 text-bold">{t("dashboard.dre.title")}</span>
        <DownloadButton
          items={[
            {
              icon: "rioglyph rioglyph-search-list ",
              onClick: () => handleExport("general"),
              title: t("dashboard.dre.downloadOption.overview"),
            },
            {
              icon: "rioglyph rioglyph-building",
              onClick: () => handleExport("branch"),
              title: t("dashboard.dre.downloadOption.branch"),
            },
            {
              icon: "rioglyph rioglyph-role-management",
              onClick: () => handleExport("segment"),
              title: t("dashboard.dre.downloadOption.segment"),
            },
            {
              icon: "rioglyph rioglyph-truck",
              onClick: () => handleExport("vehicle"),
              title: t("dashboard.dre.downloadOption.vehicle"),
            },
            {
              icon: "rioglyph rioglyph-driver",
              onClick: () => handleExport("driver"),
              title: t("dashboard.dre.downloadOption.driver"),
            },
          ]}
        />
      </div>

      <div className="overflow-x-auto">
        <div className="min-width-500">
          <div className="display-flex align-items-center justify-content-between gap-10 padding-y-10 text-color-gray text-size-12 text-size-14-sm">
            <div
              className="flex-1-1 min-width-0 display-flex align-items-center gap-5"
              style={getIndentStyle(0, indent)}
            >
              <span style={DRE_TOGGLE_COLUMN_STYLE} />
              {t("dashboard.dre.column.item")}
            </div>
            <div className="flex-0 width-100 width-150-sm text-right">{t("dashboard.dre.column.forecast")}</div>
            <div className="flex-0 width-100 width-150-sm text-right">{t("dashboard.dre.column.realized")}</div>
            <div className="flex-0 width-100 width-150-sm text-right">
              {t("dashboard.dre.column.forecastVsRealized")}
            </div>
          </div>

          {data.map((item, index) => (
            <DRERow
              item={item}
              key={item.id}
              lineType={item.lineType}
              onToggle={onToggle}
              expandedItems={expandedItems}
              loadingNodes={loadingNodes}
              onExpandNode={onExpandNode}
              indent={indent}
              isLast={index === data.length - 1}
              depth={item.level}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}
